import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyOTP, verifyUnverified, resendVerification, fetchUser } from '../../store/slices/authSlice';
import { Form, Button, Container, Card, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import {
    FaHeart,
    FaEnvelope,
    FaPhone,
    FaSpinner,
    FaCheckCircle,
    FaShieldAlt,
    FaLock,
    FaClock,
    FaBolt
} from 'react-icons/fa';
import './Auth.css';

const Verification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, user } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [sentType, setSentType] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const identifierFromQuery = searchParams.get('identifier');
  const isUnverified = !!identifierFromQuery;

  // Determine verification type based on user data
  const verificationType = sentType || (isUnverified ? (identifierFromQuery.includes('@') ? 'email' : 'phone') : (user?.email ? 'email' : 'phone'));
  const identifier = identifierFromQuery || user?.email || user?.phone;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isUnverified) {
        await dispatch(verifyUnverified({ identifier, code })).unwrap();
      } else {
        await dispatch(verifyOTP({ type: verificationType, code })).unwrap();
      }
      setSuccessMessage('Vérification effectuée avec succès !');
      setTimeout(() => navigate('/complete-profile'), 2000);
    } catch (err) {
      console.error('Verification failed:', err);
    }
  };

  const handleResend = async () => {
    try {
      const result = await dispatch(resendVerification(identifier)).unwrap();
      setSentType(result.type);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      console.error('Resend failed:', err);
    }
  };

  return (
    <div className="auth-page">
      <Container>
        <Row className="justify-content-center py-5">
          <Col lg={10} xl={12}>
            <Card className="auth-card shadow-lg">
              <Row className="g-0">
                {/* Left Side - Security Info */}
                <Col md={6} className="auth-info-section">
                  <div className="auth-info-content">
                    <div className="auth-logo">
                      <FaHeart className="logo-icon" />
                      <h1>💕 CHATOO</h1>
                    </div>
                    <h2 className="auth-info-title">
                      Sécurisez votre compte
                    </h2>
                    <p className="auth-info-text">
                      Vérifiez votre identité pour accéder à votre espace personnel et
                      rencontrer des âmes sœurs authentiques.
                    </p>

                    <div className="auth-features">
                      <div className="auth-feature">
                        <FaShieldAlt className="feature-icon" />
                        <span>Vérification sécurisée</span>
                      </div>
                      <div className="auth-feature">
                        <FaLock className="feature-icon" />
                        <span>Protection des données</span>
                      </div>
                      <div className="auth-feature">
                        <FaClock className="feature-icon" />
                        <span>Code temporaire valide</span>
                      </div>
                    </div>

                    <div className="auth-stats">
                      <div className="stat-item">
                        <FaCheckCircle className="stat-icon-svg" />
                        <h3>99.9%</h3>
                        <p>Fiabilité</p>
                      </div>
                      <div className="stat-item">
                        <FaBolt className="stat-icon-svg" />
                        <h3>{'<'} 30s</h3>
                        <p>Vérification</p>
                      </div>
                      <div className="stat-item">
                        <FaShieldAlt className="stat-icon-svg" />
                        <h3>100%</h3>
                        <p>Sécurisé</p>
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Right Side - Form */}
                <Col md={6} className="auth-form-section">
                  <div className="auth-form-content">
                    <h3 className="auth-form-title">Vérification du compte 💕</h3>
                    <p className="auth-form-subtitle">
                      Un code de vérification a été envoyé à votre {verificationType === 'email' ? 'email' : 'téléphone'} :
                      <br />
                      <strong>{identifier}</strong>
                    </p>

                    {/* Error Message */}
                    {error && (
                      <Alert variant="danger" className="auth-alert">
                        <strong>Erreur :</strong> {error.message || 'Code invalide ou expiré'}
                      </Alert>
                    )}

                    {/* Success Message */}
                    {successMessage && (
                      <Alert variant="success" className="auth-alert">
                        {successMessage}
                      </Alert>
                    )}

                    {/* Resend Success */}
                    {resendSuccess && (
                      <Alert variant="success" className="auth-alert">
                        Code de vérification envoyé avec succès !
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit} className="auth-form">
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label-custom">
                          {verificationType === 'email' ? <FaEnvelope className="me-2" /> : <FaPhone className="me-2" />}
                          Code de vérification
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            maxLength="6"
                            required
                            className="form-control-custom"
                            placeholder="Entrez le code à 6 chiffres"
                          />
                        </InputGroup>
                      </Form.Group>

                      <Button
                        type="submit"
                        className="btn-auth-submit w-100"
                        disabled={loading || code.length !== 6}
                      >
                        {loading ? (
                          <>
                            <FaSpinner className="spinner-border spinner-border-sm me-2" />
                            Vérification en cours...
                          </>
                        ) : (
                          <>
                            <FaBolt className="me-2" />
                            Vérifier mon compte 💕
                          </>
                        )}
                      </Button>
                    </Form>

                    <div className="auth-divider">
                      <span>OU</span>
                    </div>

                    <div className="resend-verification">
                      <p>Pas reçu le code ?</p>
                      <Button
                        variant="outline"
                        onClick={handleResend}
                        className="btn-chatoo-secondary w-100"
                        disabled={loading}
                      >
                        {loading ? <FaSpinner className="spinner me-2" /> : <FaEnvelope className="me-2" />}
                        Renvoyer le code
                      </Button>
                    </div>

                    <div className="auth-footer">
                      <p>
                        Retour à{' '}
                        <Button
                          variant="link"
                          onClick={() => navigate('/register')}
                          className="auth-link p-0"
                        >
                          l'inscription
                        </Button>
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Background Decoration */}
      <div className="auth-background">
        <div className="floating-heart heart-1">❤️</div>
        <div className="floating-heart heart-2">💕</div>
        <div className="floating-heart heart-3">💖</div>
        <div className="floating-heart heart-4">💗</div>
        <div className="floating-heart heart-5">💓</div>
        <div className="floating-heart heart-6">💘</div>
      </div>
    </div>
  );
};

export default Verification;
